import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../utils";

type FiltersDropdownProps = {
  title?: string;
  ariaLabel?: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  activeFiltersCount?: number;
};

type ActiveRowContextValue = {
  activeRowId: string | undefined;
  setActiveRowId: (id: string | undefined) => void;
};

const ActiveRowContext = createContext<ActiveRowContextValue | undefined>(
  undefined,
);

/**
 * Which nested filter row (by its rowId) is the currently expanded/"active"
 * one among siblings inside a FiltersDropdown. Returns undefined when called
 * outside a FiltersDropdown, so consumers can fall back to standalone behavior.
 */
export const useFiltersDropdownActiveRow = () => useContext(ActiveRowContext);

type ActiveRowProviderProps = {
  open: boolean;
  children: ReactNode;
};

const ActiveRowProvider = ({ open, children }: ActiveRowProviderProps) => {
  const [activeRowId, setActiveRowId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) setActiveRowId(undefined);
  }, [open]);

  return (
    <ActiveRowContext.Provider value={{ activeRowId, setActiveRowId }}>
      {children}
    </ActiveRowContext.Provider>
  );
};

/**
 * A popover-based filter panel trigger.
 * Renders a ghost button with title + optional icon; children populate the popover content.
 * When activeFiltersCount is greater than 0, it's appended to the title, e.g. "Filters (2)".
 */
export const FiltersDropdown = ({
  title,
  ariaLabel,
  icon,
  children,
  className: propsClassName,
  open,
  onOpenChange,
  activeFiltersCount,
}: FiltersDropdownProps) => {
  const [mirrorOpen, setMirrorOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setMirrorOpen(next);
    onOpenChange?.(next);
  };

  const label = activeFiltersCount ? `${title} (${activeFiltersCount})` : title;

  const contentClassName = cn(
    "z-50 min-w-[190px] w-auto overflow-visible rounded-md p-1",
    "border border-border-interactive-neutral-idle bg-fill-interactive-neutral-idle text-foreground shadow-md",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
    propsClassName,
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="gap-2 group"
          size="sm"
          aria-label={
            ariaLabel ?? (typeof title === "string" ? title : undefined)
          }
        >
          <span className="shrink-0 [&>svg]:size-4 [&>svg]:text-icon-panel-neutral-mild group-hover:[&>svg]:text-icon-interactive-neutral-hover">
            {icon}
          </span>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className={contentClassName}>
        <ActiveRowProvider open={open ?? mirrorOpen}>
          {children}
        </ActiveRowProvider>
      </PopoverContent>
    </Popover>
  );
};
